/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import { List, Set } from 'immutable'
import * as R from 'ramda'
import { getDisplayName } from '../../utils/react-utils'

// Types definition
type WrapListDefaults<F, V> = Required<
    Pick<
        WrapListProps<F, V>,
        'listActions' | 'isLoading' | 'isAdding' | 'resetStates' | 'shouldUpdate'
    >
>

export type GrabListProps<F = string | object, V = any> = {
    listId: string
    filterData: F
    filterValue: V
    resetStates?: boolean
    shouldUpdate?: (props: object, next: object) => boolean
    listActions: {
        showByState(state: F, value: V, t?: number): void | Promise<any>
        showByStates(state: F, t?: number): void | Promise<any>
        showByResetStates(state: F, t?: number): void | Promise<any>
    }
}

type WrapListProps<F = string | object, V = any> = {
    initialized: boolean
    ids: List<number | string> | Set<number | string> | Array<number | string>
    isLoading: boolean
    isAdding: boolean
} & GrabListProps<F, V>

/**
 * Component Enhancer (HOC) for manage the results states
 * @param {Component} WrappedList
 * @return {WrapperComponent} WrapList
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function controlListState<
    F = string | object,
    V = any,
    TProps extends WrapListProps<F, V> = WrapListProps<F, V>
>(WrappedList: React.ComponentType<TProps>) {
    return class WrapList extends React.Component<TProps> {
        public static displayName = `controlListState(${getDisplayName(WrappedList)})`

        public static defaultProps: WrapListDefaults<F, V> = {
            listActions: {
                showByState: () => {},
                showByStates: () => {},
                showByResetStates: () => {}
            },
            isLoading: false,
            isAdding: false,
            resetStates: false,
            shouldUpdate: () => false
        }

        public componentDidMount(): void {
            this.refreshResults(this.props)
        }

        public componentDidUpdate(prevProps: Readonly<TProps>): void {
            const { filterData, filterValue } = this.props

            if (
                !R.equals(filterData, prevProps.filterData) ||
                filterValue !== prevProps.filterValue
            ) {
                this.refreshResults(this.props, 100)
            }
        }

        public shouldComponentUpdate(nextProps: Readonly<TProps>): boolean {
            const { ids, isLoading, isAdding, shouldUpdate } = this.props

            return (
                !List(ids).equals(List(nextProps.ids)) ||
                isLoading !== nextProps.isLoading ||
                isAdding !== nextProps.isAdding ||
                (typeof shouldUpdate === 'function' && shouldUpdate(this.props, nextProps))
            )
        }
        // FIXME: set this method as private access when typescript fix it
        refreshResults(props: Readonly<TProps>, timeout = 0): void {
            const { listActions, filterData, filterValue, resetStates } = props

            if (typeof filterData === 'object') {
                if (resetStates) {
                    listActions.showByResetStates(filterData, timeout)
                } else {
                    listActions.showByStates(filterData, timeout)
                }
            } else if (filterData && filterValue) {
                listActions.showByState(filterData, filterValue, timeout)
            }
        }

        public render(): React.ReactNode {
            const { ids, ...props } = this.props

            return <WrappedList {...(props as TProps)} ids={List(ids).toArray()} />
        }
    }
}
